import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// src/ui/MobileNav.tsx — WCAG-AA compliant mobile nav with focus trap + Escape-to-close.
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
export function MobileNav({ items, brand = 'ReviewRadar' }) {
    const [open, setOpen] = useState(false);
    const firstLinkRef = useRef(null);
    const triggerRef = useRef(null);
    const loc = useLocation();
    // Close on route change
    useEffect(() => { setOpen(false); }, [loc.pathname]);
    // Escape closes, returns focus to trigger
    useEffect(() => {
        if (!open)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
                triggerRef.current?.focus();
            }
            // Rudimentary focus trap: if Tab from last link, cycle to first
            if (e.key === 'Tab') {
                const root = document.getElementById('mobile-nav-panel');
                if (!root)
                    return;
                const focusables = root.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])');
                if (focusables.length === 0)
                    return;
                const first = focusables[0], last = focusables[focusables.length - 1];
                if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                }
            }
        };
        document.addEventListener('keydown', onKey);
        // Focus first link when panel opens
        queueMicrotask(() => firstLinkRef.current?.focus());
        // Lock scroll
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
    }, [open]);
    return (_jsxs(_Fragment, { children: [_jsx("button", { ref: triggerRef, type: "button", "aria-expanded": open, "aria-controls": "mobile-nav-panel", "aria-label": open ? 'Close menu' : 'Open menu', onClick: () => setOpen(v => !v), className: "md:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500", children: open ? _jsx(X, { "aria-hidden": "true" }) : _jsx(Menu, { "aria-hidden": "true" }) }), open && (_jsxs("div", { id: "mobile-nav-panel", role: "dialog", "aria-modal": "true", "aria-label": "Navigation", className: "fixed inset-0 z-50 md:hidden", children: [_jsx("button", { "aria-hidden": "true", tabIndex: -1, onClick: () => setOpen(false), className: "absolute inset-0 bg-slate-900/40" }), _jsxs("nav", { "aria-label": "Primary", className: "absolute left-0 top-0 bottom-0 w-[86%] max-w-xs bg-white shadow-2xl p-6 overflow-y-auto", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("span", { className: "text-lg font-semibold tracking-tight text-slate-900", children: brand }), _jsx("button", { type: "button", "aria-label": "Close menu", onClick: () => { setOpen(false); triggerRef.current?.focus(); }, className: "inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500", children: _jsx(X, { "aria-hidden": "true", size: 18 }) })] }), _jsx("ul", { className: "mt-6 space-y-1", children: items.map((item, i) => (_jsx("li", { children: _jsx(Link, { ref: i === 0 ? firstLinkRef : undefined, to: item.to, className: 'block rounded-lg px-3 py-2 text-sm font-medium ' +
                                            (loc.pathname === item.to
                                                ? 'bg-brand-50 text-brand-700'
                                                : 'text-slate-700 hover:bg-slate-100') +
                                            ' focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500', "aria-current": loc.pathname === item.to ? 'page' : undefined, children: item.label }) }, item.to))) })] })] }))] }));
}
